import { useEffect, useState } from "react";
import clsx from "clsx";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { MailFilePreview } from "@/projectOverview/MailFilePreview";
import { MontagePdfEmbed } from "@/projectOverview/MontagePdfEmbed";
import { noteNeedsReprint } from "@/projectOverview/montageNotePrintState";
import {
  COVER_PRINT_LOG_CHANGED_EVENT,
  getCoverPrintLog,
  latestCoverPrint,
} from "@/lib/coverPrintLog";
import {
  createMontageunterlageNote,
  fetchMontageAbOhnePreisePdf,
  fetchMontageDeckblattPdf,
  fetchMontageunterlageFileBlob,
  getMontageAbAvailability,
  getMontageunterlagen,
  isAufmassPackItem,
  isMailMontageFile,
  isWerkstattPackItem,
  resolveMontageunterlageFileUrl,
  type Montageunterlage,
} from "@/lib/projectOverview";
import type { PlanungOrderLink } from "@/lib/montageplanung";
import { splitOrderLinkLabel } from "./displayLabels";
import { MP_DIALOG_Z_INDEX_CLASS } from "./layoutConstants";

interface OrderPack {
  order: PlanungOrderLink;
  items: Montageunterlage[];
  hasAb: boolean;
  printedAt: string | null;
  error: string | null;
}

type PdfPreview = {
  kind: "pdf";
  key: string;
  title: string;
  loadPdf: () => Promise<Blob>;
  emptyMessage?: string;
};

type ImagePreview = {
  kind: "image";
  key: string;
  title: string;
  url: string;
  alt: string;
};

type MailPreview = {
  kind: "mail";
  key: string;
  title: string;
  orderId: number;
  itemId: number;
};

type DocPreview = PdfPreview | ImagePreview | MailPreview;

function isImage(item: Montageunterlage): boolean {
  return String(item.content_type || "").toLowerCase().startsWith("image/");
}

function isPdf(item: Montageunterlage): boolean {
  const type = String(item.content_type || "").toLowerCase();
  if (type.includes("pdf")) return true;
  return String(item.filename || "").toLowerCase().endsWith(".pdf");
}

function fileLabel(item: Montageunterlage): string {
  const title = item.title || item.filename || "Datei";
  return isWerkstattPackItem(item) ? `Werkstatt · ${title}` : title;
}

function DocChip({
  children,
  onClick,
}: {
  children: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="max-w-full truncate rounded-lg bg-card px-2 py-1 text-left text-xs font-medium text-label ring-1 ring-inset ring-separator transition hover:bg-fill-tertiary"
      title={children}
    >
      {children}
    </button>
  );
}

export function MontageunterlageNoteComposer({
  orders,
}: {
  orders: PlanungOrderLink[];
}) {
  const [noteOrderId, setNoteOrderId] = useState<number | null>(null);
  const [noteText, setNoteText] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const orderKey = orders.map((order) => order.order_id).join(",");

  useEffect(() => {
    if (!orders.some((order) => order.order_id === noteOrderId)) {
      setNoteOrderId(orders[0]?.order_id ?? null);
    }
  }, [orderKey, noteOrderId, orders]);

  async function submitNote() {
    const orderId = noteOrderId ?? orders[0]?.order_id;
    const body = noteText.trim();
    if (!orderId || !body || noteSaving) return;
    setNoteSaving(true);
    setNoteError(null);
    try {
      await createMontageunterlageNote(orderId, { body_text: body });
      setNoteText("");
      window.dispatchEvent(new Event("montageunterlagen_changed"));
    } catch (err) {
      setNoteError(err instanceof Error ? err.message : "Notiz konnte nicht gespeichert werden.");
    } finally {
      setNoteSaving(false);
    }
  }

  return (
    <div className="space-y-2">
      {orders.length === 0 ? (
        <p className="text-xs leading-relaxed text-label-secondary">
          Verknüpfen Sie zuerst einen Auftrag. Die Notiz wird dann an diesem Projekt gespeichert und auf dem Deckblatt gedruckt.
        </p>
      ) : (
        <form
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            void submitNote();
          }}
        >
          {orders.length > 1 ? (
            <select
              className="eh-input"
              aria-label="Auftrag für die Notiz"
              value={noteOrderId ?? ""}
              onChange={(event) => setNoteOrderId(Number(event.target.value))}
            >
              {orders.map((order) => {
                const { number } = splitOrderLinkLabel(order.order_id, order.order_label);
                return (
                  <option key={order.order_id} value={order.order_id}>
                    {number}
                  </option>
                );
              })}
            </select>
          ) : null}
          <textarea
            className="eh-input min-h-[4.5rem]"
            rows={2}
            value={noteText}
            onChange={(event) => setNoteText(event.target.value)}
            placeholder="Was soll vor Ort erledigt werden?"
            aria-label="Montageunterlagennotiz"
          />
          <div className="flex items-center justify-between gap-2">
            {noteError ? <p className="min-w-0 text-xs text-status-red">{noteError}</p> : <span />}
            <Button type="submit" size="sm" loading={noteSaving} disabled={!noteText.trim()}>
              Notiz hinzufügen
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

export function AssignmentMontageunterlagen({
  orders,
  active,
}: {
  orders: PlanungOrderLink[];
  active: boolean;
}) {
  const [packs, setPacks] = useState<OrderPack[]>([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<DocPreview | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const orderKey = orders.map((order) => order.order_id).join(",");

  useEffect(() => {
    if (!active) return;
    function refresh() {
      setReloadKey((value) => value + 1);
    }
    window.addEventListener("montageunterlagen_changed", refresh);
    window.addEventListener(COVER_PRINT_LOG_CHANGED_EVENT, refresh);
    return () => {
      window.removeEventListener("montageunterlagen_changed", refresh);
      window.removeEventListener(COVER_PRINT_LOG_CHANGED_EVENT, refresh);
    };
  }, [active]);

  useEffect(() => {
    if (!active || orders.length === 0) {
      setPacks([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void Promise.all(
      orders.map(async (order) => {
        try {
          const [items, ab, prints] = await Promise.all([
            getMontageunterlagen(order.order_id),
            getMontageAbAvailability(order.order_id).catch(() => ({ exists: false })),
            getCoverPrintLog(order.order_id).catch(() => []),
          ]);
          return {
            order,
            items: items.filter((item) => !isAufmassPackItem(item)),
            hasAb: Boolean(ab.exists),
            printedAt: latestCoverPrint(prints, "montage")?.created_at ?? null,
            error: null,
          } satisfies OrderPack;
        } catch (err) {
          return {
            order,
            items: [],
            hasAb: false,
            printedAt: null,
            error: err instanceof Error ? err.message : "Unterlagen konnten nicht geladen werden.",
          } satisfies OrderPack;
        }
      }),
    ).then((next) => {
      if (cancelled) return;
      setPacks(next);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [active, orderKey, orders, reloadKey]);

  function openFile(orderId: number, item: Montageunterlage) {
    const title = fileLabel(item);
    if (isImage(item)) {
      setPreview({
        kind: "image",
        key: `image-${item.id}`,
        title,
        url: resolveMontageunterlageFileUrl(item, orderId),
        alt: item.filename || title,
      });
      return;
    }
    if (isPdf(item)) {
      setPreview({
        kind: "pdf",
        key: `file-${item.id}`,
        title,
        loadPdf: () => fetchMontageunterlageFileBlob(orderId, item),
      });
      return;
    }
    if (isMailMontageFile(item)) {
      setPreview({
        kind: "mail",
        key: `mail-${item.id}`,
        title,
        orderId,
        itemId: item.id,
      });
      return;
    }
    window.open(resolveMontageunterlageFileUrl(item, orderId), "_blank", "noopener,noreferrer");
  }

  return (
    <section className="flex min-h-64 flex-1 flex-col overflow-hidden rounded-2xl bg-fill-quaternary/50 p-3 ring-1 ring-inset ring-separator lg:min-h-0">
      <div className="mb-2 flex shrink-0 items-baseline justify-between gap-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-label-tertiary">
          Montageunterlagen
        </h2>
        {orders.length > 0 && !loading ? (
          <span className="text-[11px] text-label-tertiary">
            {orders.length === 1 ? "1 Auftrag" : `${orders.length} Aufträge`}
          </span>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-0.5">
        {orders.length === 0 ? (
          <p className="text-xs leading-relaxed text-label-secondary">
            Sobald ein Auftrag verknüpft ist, stehen Notizen, Adressen und Dokumente hier zum Ansehen.
          </p>
        ) : null}

        {loading && orders.length > 0 ? (
          <p className="text-xs text-label-secondary">Unterlagen werden geladen…</p>
        ) : null}

        {!loading &&
          packs.map((pack) => {
            const { number, details } = splitOrderLinkLabel(pack.order.order_id, pack.order.order_label);
            const notes = pack.items.filter((item) => item.entry_type === "note" && item.body_text?.trim());
            const addresses = pack.items.filter(
              (item) => item.entry_type === "address" && (item.body_text?.trim() || item.maps_url),
            );
            const files = pack.items.filter((item) => item.entry_type === "file");
            const unprintedNotes = notes.filter((item) => noteNeedsReprint(item, pack.printedAt));
            return (
              <article key={pack.order.order_id} className="rounded-xl bg-card px-3 py-2.5 ring-1 ring-inset ring-separator">
                <p className="font-mono text-[13px] font-semibold text-label">{number}</p>
                {details ? (
                  <p className="mt-0.5 text-xs leading-snug text-label-secondary">{details}</p>
                ) : null}

                {pack.error ? (
                  <p className="mt-1 text-xs text-status-red">{pack.error}</p>
                ) : (
                  <div className="mt-2 space-y-2">
                    {unprintedNotes.length > 0 ? (
                      <p className="rounded-lg bg-status-red/10 px-2 py-1.5 text-xs leading-snug text-status-red">
                        {unprintedNotes.length === 1
                          ? "Eine Notiz ist seit dem Deckblatt-Druck neu. Bitte das Deckblatt neu drucken."
                          : `${unprintedNotes.length} Notizen sind seit dem Deckblatt-Druck neu. Bitte das Deckblatt neu drucken.`}
                      </p>
                    ) : null}
                    {notes.length === 0 ? (
                      <p className="text-xs text-label-secondary">Noch keine Montageunterlagennotiz.</p>
                    ) : null}
                    {notes.map((item) => (
                      <div key={item.id} className="rounded-lg bg-fill-quaternary/60 px-2.5 py-2">
                        <p className="flex flex-wrap items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-label-tertiary">
                          <span>
                            {isWerkstattPackItem(item) ? "Werkstatt" : "Notiz"}
                            {item.title ? ` · ${item.title}` : ""}
                          </span>
                          {noteNeedsReprint(item, pack.printedAt) ? (
                            <span className="rounded-full bg-status-red/15 px-1.5 py-0.5 text-[10px] normal-case tracking-normal text-status-red">
                              Nicht gedruckt
                            </span>
                          ) : null}
                        </p>
                        <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-label">
                          {item.body_text}
                        </p>
                      </div>
                    ))}
                    {addresses.map((item) => (
                      <div key={item.id}>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-label-tertiary">
                          Adresse{item.title ? ` · ${item.title}` : ""}
                        </p>
                        {item.body_text ? (
                          <p className="mt-0.5 whitespace-pre-wrap text-xs leading-relaxed text-label">
                            {item.body_text}
                          </p>
                        ) : null}
                        {item.maps_url ? (
                          <a
                            href={item.maps_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-0.5 inline-flex text-xs font-medium text-accent hover:underline"
                          >
                            In Karten öffnen
                          </a>
                        ) : null}
                      </div>
                    ))}
                    <div className="space-y-1.5 border-t border-separator pt-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-label-tertiary">
                        Dokumente
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                      <DocChip
                        onClick={() =>
                          setPreview({
                            kind: "pdf",
                            key: `deckblatt-${pack.order.order_id}`,
                            title: `Deckblatt ${number}`,
                            loadPdf: () => fetchMontageDeckblattPdf(pack.order.order_id),
                          })
                        }
                      >
                        {unprintedNotes.length > 0 ? "Deckblatt neu drucken" : "Deckblatt"}
                      </DocChip>
                      {pack.hasAb ? (
                        <DocChip
                          onClick={() =>
                            setPreview({
                              kind: "pdf",
                              key: `ab-${pack.order.order_id}`,
                              title: `Auftragsbestätigung ${number}`,
                              loadPdf: () => fetchMontageAbOhnePreisePdf(pack.order.order_id),
                              emptyMessage: "Noch keine Auftragsbestätigung vorhanden.",
                            })
                          }
                        >
                          Auftragsbestätigung
                        </DocChip>
                      ) : null}
                      {files.map((item) => (
                        <DocChip key={item.id} onClick={() => openFile(pack.order.order_id, item)}>
                          {fileLabel(item)}
                        </DocChip>
                      ))}
                      </div>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
      </div>

      <Dialog
        open={preview != null}
        onClose={() => setPreview(null)}
        title={preview?.title ?? "Montageunterlage"}
        size="xl"
        zIndexClass={MP_DIALOG_Z_INDEX_CLASS}
        panelClassName="lg:h-[min(88vh,860px)]"
        bodyClassName="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:overflow-hidden"
      >
        {preview?.kind === "pdf" ? (
          <MontagePdfEmbed
            loadKey={preview.key}
            loadPdf={preview.loadPdf}
            title={preview.title}
            emptyMessage={preview.emptyMessage}
            className="!min-h-0 h-full flex-1"
          />
        ) : null}
        {preview?.kind === "mail" ? (
          <MailFilePreview orderId={preview.orderId} itemId={preview.itemId} />
        ) : null}
        {preview?.kind === "image" ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <img
              src={preview.url}
              alt={preview.alt}
              crossOrigin="use-credentials"
              className={clsx("max-h-[70vh] max-w-full rounded-xl object-contain ring-1 ring-separator")}
            />
          </div>
        ) : null}
      </Dialog>
    </section>
  );
}
